/* Live booking social-proof toast.
   Cycles through real recent bookings (or curated fallback). Shown bottom-left,
   non-intrusive, dismissable. Powered by GET /api/social-proof/recent. */
import { useEffect, useState } from "react";
import { CheckCircle2, X } from "lucide-react";
import { api } from "@/lib/api";

export default function LiveBookingToast() {
    const [items, setItems] = useState([]);
    const [idx, setIdx] = useState(0);
    const [visible, setVisible] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        api.get("/social-proof/recent")
            .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (dismissed || items.length === 0) return;
        let cancelled = false;
        const cycle = async () => {
            await new Promise((r) => setTimeout(r, 8000));
            if (cancelled || dismissed) return;
            setVisible(true);
            await new Promise((r) => setTimeout(r, 5500));
            if (cancelled || dismissed) return;
            setVisible(false);
            await new Promise((r) => setTimeout(r, 9000));
            if (cancelled || dismissed) return;
            setIdx((i) => (i + 1) % items.length);
            cycle();
        };
        cycle();
        return () => { cancelled = true; };
    }, [items, dismissed]);

    if (dismissed || items.length === 0) return null;
    const item = items[idx];
    const minsAgo = 3 + Math.floor(Math.random() * 27);

    return (
        <div
            data-testid="live-booking-toast"
            className={`fixed left-4 bottom-24 z-30 max-w-[19rem] transition-all duration-500 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
            }`}
        >
            <div className="rounded-2xl border border-gold/30 bg-ink-deep/95 backdrop-blur-xl shadow-2xl p-4 pr-6">
                <button
                    onClick={() => setDismissed(true)}
                    className="absolute top-2 right-2 text-cream-dim hover:text-gold"
                    aria-label="Dismiss"
                    data-testid="live-toast-close"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
                <div className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-gold flex-shrink-0 mt-0.5" />
                    <div className="text-sm leading-tight">
                        <div className="text-cream">
                            <strong>{item.name}</strong> from <span className="text-cream-muted">{item.country}</span>
                        </div>
                        <div className="text-xs text-cream-muted mt-1">
                            booked <span className="text-gold">{item.service}</span>
                        </div>
                        <div className="text-[11px] text-cream-dim mt-1">{minsAgo} min ago</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
